import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Settings, MapPin, Phone, Mail, IdCard, Bike, Edit3 } from "lucide-react";

export default function DriverDetailsModal({
  open,
  onOpenChange,
  driver,
  fetchDriverConfigByCode,
  onOpenConfig, // opens config modal for this driver (same function you already use)
  onEditDriver, // ✅ NEW: opens driver edit modal from inside details modal
}) {
  const [fetching, setFetching] = useState(false);
  const [config, setConfig] = useState(null);

  const driverCode = driver?.driverId;

  const initials = useMemo(() => {
    const name = driver?.fullName || driver?.username || "";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const a = (parts[0]?.[0] || "").toUpperCase();
    const b = (parts[1]?.[0] || "").toUpperCase();
    return (a + b) || "D";
  }, [driver]);

  const photoUrl = driver?.profile_photo?.trim?.() || "";

  const fetchConfig = async () => {
    if (!driverCode) return;
    await fetchDriverConfigByCode(driverCode, setFetching, setConfig);
  };

  useEffect(() => {
    if (open) fetchConfig();
    else setConfig(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, driverCode]);

  if (!driver) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95%] max-w-5xl bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-orange-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-orange-700">
                <IdCard size={18} />
              </div>
              <div>
                <Dialog.Title className="text-base font-semibold text-gray-900">
                  Driver Details
                </Dialog.Title>
                <p className="text-xs text-gray-600">
                  {driver.driverId} • {driver.teamName || "—"} • {driver.status || "—"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* ✅ NEW: EDIT DRIVER BUTTON INSIDE MODAL */}
              <button
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border hover:bg-gray-100 text-xs text-gray-900"
                onClick={() => onEditDriver?.(driver)}
                title="Edit Driver"
              >
                <Edit3 size={14} />
                Edit Driver
              </button>

              {/* EDIT CONFIG BUTTON INSIDE MODAL (existing) */}
              <button
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs"
                onClick={() => onOpenConfig?.(driver.driverId)}
                title="Edit Configuration"
              >
                <Settings size={14} />
                Edit Config
              </button>

              <Dialog.Close asChild>
                <button className="p-2 rounded-lg hover:bg-gray-100" aria-label="Close">
                  <X size={18} />
                </button>
              </Dialog.Close>
            </div>
          </div>

          {/* HORIZONTAL BODY */}
          <div className="grid grid-cols-1 md:grid-cols-[360px_1fr]">
            {/* LEFT: ID CARD (sticky feel) */}
            <div className="border-b md:border-b-0 md:border-r p-4 bg-white">
              <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-orange-600 to-orange-500 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs opacity-90">PACKKY • DRIVER</div>
                      <div className="text-lg font-semibold leading-tight mt-1">
                        {driver.fullName || driver.username || "—"}
                      </div>
                      <div className="text-xs opacity-95 mt-1">
                        Code: <span className="font-semibold">{driver.driverId}</span>
                      </div>
                    </div>

                    <div className="w-14 h-14 rounded-2xl bg-white/20 border border-white/30 overflow-hidden flex items-center justify-center">
                      {photoUrl ? (
                        <img src={photoUrl} alt="Driver" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white font-bold">
                          {initials}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    <Tag label={`Tier: ${driver.tier || "—"}`} />
                    <Tag label={`Duty: ${driver.dutyStatus || "—"}`} />
                    <Tag label={`Type: ${driver.driver_type || "—"}`} />
                    <Tag label={`Team: ${driver.teamName || "—"}`} />
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <MiniRow
                    icon={<Phone size={14} className="text-orange-600" />}
                    label="Phone"
                    value={driver.phone || "—"}
                  />
                  <MiniRow
                    icon={<Mail size={14} className="text-orange-600" />}
                    label="Email"
                    value={driver.email || "—"}
                  />
                  <MiniRow
                    icon={<MapPin size={14} className="text-orange-600" />}
                    label="Location"
                    value={driver.location_address || "—"}
                  />
                </div>
              </div>
            </div>

            {/* RIGHT: DETAILS + CONFIG (scroll inside, not whole modal) */}
            <div className="p-4 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Personal">
                  <Row k="DB ID" v={driver.id ?? "—"} />
                  <Row k="First Name" v={driver.first_name || "—"} />
                  <Row k="Last Name" v={driver.last_name || "—"} />
                  <Row k="Username" v={driver.username || "—"} />
                </Section>

                <Section title="Work & Shift">
                  <Row k="Joining Date" v={driver.joiningDate || "—"} />
                  <Row k="Last Login" v={driver.last_login || "—"} />
                  <Row k="Shift Start" v={driver.shift_start || "—"} />
                  <Row k="Shift End" v={driver.shift_end || "—"} />
                  <Row k="Team ID" v={driver.teamid ?? "—"} />
                </Section>

                <Section title="Transport">
                  <Row
                    k="Transport Type"
                    v={
                      <span className="inline-flex items-center gap-2">
                        <Bike size={14} className="text-orange-600" />
                        {driver.transport_type || "—"}
                      </span>
                    }
                  />
                  <Row k="Transport Description" v={driver.transport_description || "—"} />
                  <Row k="Licence Plate" v={driver.licence_plate || "—"} />
                  <Row k="Driver Icon" v={driver.driverIcon ? "Available" : "—"} />
                </Section>

                <Section title="Device & GPS">
                  <Row k="Device ID" v={driver.device_id || "—"} />
                  <Row k="Device Platform" v={driver.device_platform || "—"} />
                  <Row k="Lat" v={driver.location_lat || "—"} />
                  <Row k="Lng" v={driver.location_lng || "—"} />
                </Section>
              </div>

              {/* CONFIG BOTTOM */}
              <div className="mt-4 rounded-2xl border  p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Configuration</h3>

                  {/* Edit button also available here (still inside modal) */}
                  <button
                    className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-white border hover:bg-gray-100"
                    onClick={() => onOpenConfig?.(driver.driverId)}
                  >
                    <Settings size={14} />
                    Edit Config
                  </button>
                </div>

                {fetching ? (
                  <div className="text-sm text-gray-600">Loading configuration…</div>
                ) : !config ? (
                  <div className="text-sm text-gray-600">
                    No configuration found for this driver.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Chip k="Config ID" v={config.pk_config_id} />
                    <Chip k="Cash Limit" v={config.cash_limit} />
                    <Chip k="Daily Task Limit" v={config.daily_task_limit} />
                    <Chip k="Concurrent Order" v={config.concurrent_order} />
                    <Chip k="Max Team Limit" v={config.max_team_limit} />
                    <Chip k="Team ID" v={config.team_id} />
                    <Chip k="Driver Type" v={config.driver_type || "—"} />
                    <Chip k="Created" v={config.created_on ? String(config.created_on) : "—"} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ---------- UI Helpers ---------- */

function Tag({ label }) {
  return (
    <span className="text-[11px] px-2 py-1 rounded-full bg-white/15 border border-white/25">
      {label}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border bg-white shadow-sm">
      <div className="px-4 py-3 border-b ">
        <div className="text-xs font-semibold text-gray-700">{title}</div>
      </div>
      <div className="px-4 py-3 space-y-2">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-2 text-xs">
      <span className="text-gray-500">{k}</span>
      <span className="text-gray-900 font-medium text-right break-all">
        {v === null || v === undefined || v === "" ? "—" : v}
      </span>
    </div>
  );
}

function MiniRow({ icon, label, value }) {
  return (
    <div className="rounded-xl border bg-white p-3 flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] text-gray-500">{label}</div>
        <div className="text-sm font-semibold text-gray-900 truncate">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

function Chip({ k, v }) {
  return (
    <div className="rounded-xl border bg-white px-3 py-2">
      <div className="text-[11px] text-gray-500">{k}</div>
      <div className="text-sm font-semibold text-gray-900 break-all">
        {v === null || v === undefined || v === "" ? "—" : String(v)}
      </div>
    </div>
  );
}
